import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import { workflowsApi } from "../workflows/workflowsApi";

/** Per-status document counts powering the status tabs' badges. */
export interface DocumentCounts {
  all: number;
  DRAFT: number;
  PUBLISHED: number;
  PENDING_REVIEW: number;
  READY_FOR_SIGNING: number;
  COMPLETE: number;
}

export const documentsApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "documentsApi",
    baseUrl: "/api/documents",
  }),
  reducerPath: "documentsApi",
  tagTypes: ["Document", "Document History"],
  endpoints: (builder) => ({
    getDocuments: builder.query<
      any,
      {
        include?: string;
        search?: string;
        nextId?: string;
        limit?: number;
        status?: string;
        dealId?: string;
        personId?: string;
      }
    >({
      query: (args) => {
        // Build query parameters dynamically
        const queryParams = new URLSearchParams();
        // Append parameters if they exist
        if (args.search) queryParams.append("query", args.search);
        if (args.include) queryParams.append("include", args.include);
        if (args.limit) queryParams.append("limit", args.limit.toString());
        if (args.nextId) queryParams.append("nextId", args.nextId);
        if (args.status) queryParams.append("status", args.status);
        if (args.dealId) queryParams.append("dealId", args.dealId);
        if (args.personId) queryParams.append("personId", args.personId);
        return `?${queryParams.toString()}`;
      },
      keepUnusedDataFor: 0,
    }),
    /**
     * GET /documents/counts — per-status counts for the status tabs, scoped by
     * the active search/deal/person filters (NO status param). Computed
     * server-side so the tabs never bucket a paginated page. The endpoint is
     * being added by a separate backend change and may not be deployed yet, so
     * `transformErrorResponse` degrades any failure (e.g. 404) to `undefined`
     * and the UI renders the tabs cleanly without count badges.
     */
    getDocumentCounts: builder.query<
      DocumentCounts | undefined,
      { search?: string; dealId?: string; personId?: string }
    >({
      query: (args) => {
        const queryParams = new URLSearchParams();
        if (args.search) queryParams.append("query", args.search);
        if (args.dealId) queryParams.append("dealId", args.dealId);
        if (args.personId) queryParams.append("personId", args.personId);
        const qs = queryParams.toString();
        return `/counts${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (res: { data?: DocumentCounts } | undefined) =>
        res?.data,
      transformErrorResponse: () => undefined,
      keepUnusedDataFor: 30,
    }),
    getDocumentById: builder.query<
      any,
      {
        id?: string;
        include?: string;
        token?: string;
      }
    >({
      query: (args) => {
        const { id, include, token } = args;

        const queryParams = new URLSearchParams();
        if (include) queryParams.append("include", include);
        if (token) queryParams.append("token", token);

        return `/${id}?${queryParams.toString()}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Document" }],
    }),
    getDocumentFieldsById: builder.query<any, { id?: string }>({
      query: (args) => `/${args.id}/fields`,
      keepUnusedDataFor: 0,
    }),
    getFieldRelationships: builder.query<
      any,
      { documentId: string; fieldId: string }
    >({
      query: (args) =>
        `/${args.documentId}/fields/${args.fieldId}/relationships`,
      keepUnusedDataFor: 0,
    }),
    getDocumentHistory: builder.query<any, { id: string }>({
      query: (args) => `/${args.id}/history`,
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Document History" }],
    }),
    getDocumentHistoryById: builder.query<
      any,
      { id: string; historyId?: string }
    >({
      query: (args) => `/${args.id}/history/${args.historyId}`,
      keepUnusedDataFor: 0,
    }),
    patchDocumentById: builder.mutation<any, { id: string; data: any; workflowId?: string; token?: string }>({
      query: ({ id, data, token }) => {
        const queryParams = new URLSearchParams();
        if (token) queryParams.append("token", token);
        const queryString = queryParams.toString();
        return {
          url: `/${id}${queryString ? `?${queryString}` : ""}`,
          method: "PATCH",
          body: data,
        };
      },
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refetch the workflow to get updated data
          if (workflowId) {
            dispatch(workflowsApi.util.invalidateTags(['Workflow']));
          }
        } catch {
          // Error already handled
        }
      },
    }),
    updateDocumentById: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PUT",
        body: data,
      }),
    }),
    publishDocumentById: builder.mutation<any, { id: string; data: any; workflowId?: string }>({
      query: ({ id, data }) => ({
        url: `/${id}/publish`,
        method: "POST",
        body: data,
      }),
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refetch the workflow to get updated data
          if (workflowId) {
            dispatch(workflowsApi.util.invalidateTags(['Workflow']));
          }
        } catch {
          // Error already handled
        }
      },
    }),
    getFieldUses: builder.query<any, { documentId: string; fieldId: string }>({
      query: (args) => `${args.documentId}/fields/${args.fieldId}/uses`,
      keepUnusedDataFor: 0,
    }),
    addDocumentParticipants: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/${id}/participants`,
        method: "POST",
        body: data,
      }),
    }),
    updateDocumentParticipants: builder.mutation<
      any,
      { id: string; pId: string; data: any }
    >({
      query: ({ id, pId, data }) => ({
        url: `/${id}/participants/${pId}`,
        method: "PUT",
        body: data,
      }),
    }),
    removeDocumentParticipants: builder.mutation<
      any,
      { id: string; personId: string }
    >({
      query: ({ id, personId }) => ({
        url: `/${id}/participants/${personId}`,
        method: "DELETE",
      }),
    }),
    updateDocumentAssignee: builder.mutation<
      any,
      { id: string; teamId: string; workflowId?: string }
    >({
      query: ({ id, teamId }) => ({
        url: `/${id}/assignee`,
        method: "PUT",
        body: { id: teamId },
      }),
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refetch the workflow to get updated data
          if (workflowId) {
            dispatch(workflowsApi.util.invalidateTags(['Workflow']));
          }
        } catch {
          // Error already handled
        }
      },
    }),
    deleteDocumentAssignee: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}/assignee`,
        method: "DELETE",
      }),
    }),
    deleteDocument: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
    }),
    updateDocumentSignature: builder.mutation<
      any,
      { id: string; body: any; token?: string }
    >({
      query: ({ id, body, token }) => {
        const queryParams = new URLSearchParams();
        if (token) queryParams.append("token", token);

        return {
          url: `/${id}/signature?${queryParams.toString()}`,
          method: "POST",
          body,
        };
      },
    }),

    updateDocumentField: builder.mutation<
      any,
      {
        documentId: string;
        fieldId: string;
        body: any;
        updateWorkflow?: boolean;
      }
    >({
      query: ({ documentId, fieldId, body, updateWorkflow = false }) => ({
        url: `/${documentId}/fields/${fieldId}${
          updateWorkflow ? `?updateWorkflow=${updateWorkflow}` : ""
        }`,
        method: "PUT",
        body,
      }),
    }),

    createDocumentField: builder.mutation<
      any,
      {
        documentId: string;
        fieldTemplateId: string;
        body: any;
        predict?: boolean;
      }
    >({
      query: ({ documentId, fieldTemplateId, body, predict }) => ({
        url: `/${documentId}/fields/template/${fieldTemplateId}${
          predict ? "?predict=true" : ""
        }`,
        method: "POST",
        body,
      }),
    }),

    deleteDocumentField: builder.mutation<
      any,
      {
        documentId: string;
        fieldId: string;
      }
    >({
      query: ({ documentId, fieldId }) => ({
        url: `/${documentId}/fields/${fieldId}`,
        method: "DELETE",
      }),
    }),

    updateDocumentFieldsBulk: builder.mutation<
      any,
      {
        documentId: string;
        body: any[];
        updateWorkflow?: boolean;
      }
    >({
      query: ({ documentId, body, updateWorkflow = false }) => ({
        url: `/${documentId}/fields${
          updateWorkflow ? `?updateWorkflow=${updateWorkflow}` : ""
        }`,
        method: "PATCH",
        body,
      }),
    }),

    createFromTemplate: builder.mutation<
      any,
      { documentId: string; documentTemplateId: string }
    >({
      query: ({ documentId, documentTemplateId }) => ({
        url: `/${documentId}/create-from-template/${documentTemplateId}`,
        method: "POST",
      }),
    }),

    unmarkDocumentComplete: builder.mutation<
      any,
      { id: string; workflowId?: string }
    >({
      query: ({ id }) => ({
        url: `/${id}/unmark-complete`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Document" }],
      async onQueryStarted({ workflowId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (workflowId) {
            dispatch(workflowsApi.util.invalidateTags(["Workflow"]));
          }
        } catch {
          // Error already handled
        }
      },
    }),

    submitDocumentForReview: builder.mutation<
      any,
      { id: string; status: string; reviewerIds?: string[] }
    >({
      query: ({ id, status, reviewerIds }) => ({
        url: `/${id}/review`,
        method: "POST",
        body: { status, reviewerIds },
      }),
      invalidatesTags: [{ type: "Document" }],
    }),

    getFieldsByType: builder.query<
      { data: any[] },
      { projectId: string; fieldType: string }
    >({
      query: ({ projectId, fieldType }) =>
        `/projects/${projectId}/fields/${fieldType}`,
      keepUnusedDataFor: 300, // Cache for 5 minutes since this doesn't change often
    }),
  }),
});

export const {
  useGetDocumentsQuery,
  useGetDocumentCountsQuery,
  useGetDocumentByIdQuery,
  useGetDocumentFieldsByIdQuery,
  useGetFieldRelationshipsQuery,
  useGetDocumentHistoryQuery,
  useGetDocumentHistoryByIdQuery,
  useLazyGetDocumentHistoryByIdQuery,
  usePatchDocumentByIdMutation,
  useUpdateDocumentByIdMutation,
  usePublishDocumentByIdMutation,
  useGetFieldUsesQuery,
  useAddDocumentParticipantsMutation,
  useUpdateDocumentParticipantsMutation,
  useRemoveDocumentParticipantsMutation,
  useUpdateDocumentAssigneeMutation,
  useDeleteDocumentAssigneeMutation,
  useDeleteDocumentMutation,
  useUpdateDocumentSignatureMutation,
  useUpdateDocumentFieldMutation,
  useCreateDocumentFieldMutation,
  useDeleteDocumentFieldMutation,
  useUpdateDocumentFieldsBulkMutation,
  useCreateFromTemplateMutation,
  useSubmitDocumentForReviewMutation,
  useUnmarkDocumentCompleteMutation,
  useGetFieldsByTypeQuery,
} = documentsApi;
